import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { StatsigClient } from '@statsig/js-client';
import { useLoading } from '../shared-components/utils/hooks/use-loading';
import { StatsigContext } from './statsig-context';

export const StatsigContextProvider = ({
  statsigSdkKey = '',
  segmentAnonymousID = '',
  statsigEnvironment = 'production',
  SSOid = '',
  profile,
  children,
  featureGateNames = [],
  experimentNames = [],
  dynamicConfigNames = []
}) => {
  const [initialized, setInitialized] = useState(false);

  const [featureGates, setFeatureGates] = useState({});
  const [experiments, setExperiments] = useState({});
  const [dynamicConfigs, setDynamicConfigs] = useState({});

  const { isLoading: loading, asyncFn: initializeStatsig } = useLoading(
    async () => {
      const isHost = profile?.data?.is_host ?? false;
      const user = {
        userID: SSOid,
        customIDs: {
          segmentAnonymousID
        },
        custom: {
          isHost
        }
      };

      const options = {
        environment: {
          tier: statsigEnvironment
        }
      };

      const statsigClient = new StatsigClient(statsigSdkKey, user, options);
      await statsigClient.initializeAsync();

      setFeatureGates(
        (featureGateNames || []).reduce((acc, gateName) => {
          acc[gateName] = statsigClient.checkGate(gateName);
          return acc;
        }, {})
      );

      setExperiments(
        (experimentNames || []).reduce((acc, experimentName) => {
          const experiment = statsigClient.getExperiment(experimentName);
          const value = {
            ...(experiment?.value || {}),
            groupName: experiment?.groupName
          };
          acc[experimentName] = () => value;
          return acc;
        }, {})
      );

      setDynamicConfigs(
        (dynamicConfigNames || []).reduce((acc, dynamicConfigName) => {
          const value = statsigClient.getDynamicConfig(dynamicConfigName)?.value || {};
          acc[dynamicConfigName] = () => value;
          return acc;
        }, {})
      );

      setInitialized(true);
    }
  );

  useEffect(() => {
    const profileHasFetched = profile?.hasFetched ?? false;
    if (statsigSdkKey && profileHasFetched) {
      initializeStatsig().catch(() => setInitialized(true));
    }
  }, [statsigSdkKey, profile]);

  return (
    <StatsigContext.Provider
      value={{
        featureGates,
        experiments,
        dynamicConfigs,
        initialized,
        loading
      }}
    >
      {children}
    </StatsigContext.Provider>
  );
};

StatsigContextProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  statsigSdkKey: PropTypes.string.isRequired,
  segmentAnonymousID: PropTypes.string.isRequired,
  statsigEnvironment: PropTypes.oneOf(['development', 'staging', 'production']),
  profile: PropTypes.object.isRequired,
  SSOid: PropTypes.string,
  featureGateNames: PropTypes.arrayOf(PropTypes.string),
  experimentNames: PropTypes.arrayOf(PropTypes.string),
  dynamicConfigNames: PropTypes.arrayOf(PropTypes.string)
};
